import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Globe, Copy, CheckCircle2, ExternalLink, ChevronRight,
  Sparkles, ShieldCheck, ServerCog, Cloud,
} from "lucide-react";

/**
 * Custom Domain Guide — a copy-paste checklist that walks the owner through
 * pointing a custom domain (e.g. ccsshop.in) at:
 *   • the storefront hosted on Vercel  (frontend)
 *   • the API hosted on Railway         (backend)
 *
 * Uses only static instructions + a copy-to-clipboard helper. Nothing here
 * hits the network beyond the /api/shipping-preview-style flows.
 */

function CopyPill({ value, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          toast.success("Copied");
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error("Couldn't copy automatically — long-press the value to copy it manually.");
        }
      }}
      className="mono text-xs bg-slate-900 text-slate-100 hover:bg-slate-800 px-2.5 py-1 rounded inline-flex items-center gap-1.5"
      data-testid={testid}
      title="Copy value"
    >
      {value}
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function Section({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <Card className="border-slate-200 shadow-none" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-md bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-base">{title}</h3>
            {subtitle && <p className="text-slate-500 text-xs mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function Row({ n, children }) {
  return (
    <div className="flex gap-3 items-start">
      <div className="mono text-[10px] w-5 h-5 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0 mt-0.5">
        {n}
      </div>
      <div className="flex-1 text-sm leading-relaxed">{children}</div>
    </div>
  );
}

const RAILWAY_HEALTHCHECK = "/api/";
// Placeholder Vercel-side CNAME target. In practice Vercel gives a per-project
// value like `cname.vercel-dns.com` (apex) or `<project>.vercel.app` (www).
const VERCEL_CNAME = "cname.vercel-dns.com";

export default function DomainGuide() {
  const [domain, setDomain] = useState("ccsshop.in");
  const apex = domain.replace(/^https?:\/\//, "").replace(/\/$/, "").trim().toLowerCase();
  const www = apex ? `www.${apex}` : "";
  const apiSub = apex ? `api.${apex}` : "";

  const frontendUrl = useMemo(() => (apex ? `https://${apex}` : ""), [apex]);
  const backendUrl = useMemo(() => (apiSub ? `https://${apiSub}` : ""), [apiSub]);

  return (
    <div className="p-6 md:p-10 max-w-[1100px]" data-testid="owner-domain">
      <div className="mb-6">
        <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">Deployment</div>
        <h1 className="font-heading text-3xl font-bold mt-2 flex items-center gap-2">
          <Globe className="w-7 h-7" /> Custom domain setup
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Point your own domain (e.g. <span className="mono">ccsshop.in</span>) at the storefront
          and the API. Do the four steps below in order — total time about 15 minutes plus DNS
          propagation.
        </p>
      </div>

      {/* Domain input */}
      <Card className="border-slate-200 shadow-none mb-4">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="mono text-[11px] uppercase tracking-widest text-slate-500 md:min-w-[140px]">
            Your domain
          </div>
          <Input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="ccsshop.in"
            className="max-w-[240px]"
            data-testid="domain-input"
          />
          <div className="mono text-[11px] text-slate-500">
            Storefront →{" "}
            <span className="text-slate-800">{frontendUrl || "—"}</span>{" "}
            · API → <span className="text-slate-800">{backendUrl || "—"}</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4">
        {/* Step 1 — Buy the domain */}
        <Section
          icon={Sparkles}
          title="1. Buy the domain"
          subtitle="Skip this if you already own it."
          testid="domain-step-1"
        >
          <div className="space-y-2 text-sm text-slate-700">
            <Row n="1">
              Register <span className="mono">{apex || "your-domain.in"}</span> with any registrar —
              GoDaddy, Namecheap, Cloudflare Registrar, BigRock, Google Domains (Squarespace).
            </Row>
            <Row n="2">
              Keep the auto-renew ON so the shop stays online after year one.
            </Row>
            <Row n="3">
              For a <span className="mono">.in</span> ccTLD you may be asked for KYC — upload PAN /
              Aadhaar there itself, otherwise the domain will be suspended after 30 days.
            </Row>
          </div>
        </Section>

        {/* Step 2 — Vercel (frontend) */}
        <Section
          icon={Cloud}
          title="2. Point the storefront at Vercel (frontend)"
          subtitle="Your React app is served by Vercel — the customer-facing URL."
          testid="domain-step-2"
        >
          <div className="space-y-3 text-sm text-slate-700">
            <Row n="1">
              Open the <b>Vercel dashboard</b> →{" "}
              <a
                href="https://vercel.com/dashboard"
                target="_blank"
                rel="noreferrer"
                className="text-[#0055FF] hover:underline inline-flex items-center gap-0.5"
              >
                vercel.com/dashboard <ExternalLink className="w-3 h-3" />
              </a>{" "}
              → click the DialFlow project → <b>Settings → Domains</b>.
            </Row>
            <Row n="2">
              Click <b>Add</b> and enter <CopyPill value={apex || "your-domain.in"} testid="copy-apex" />.
              Vercel will suggest also adding the <span className="mono">www.</span> version — accept it.
            </Row>
            <Row n="3">
              Vercel will show two DNS records to add at your registrar. Copy them one by one
              and paste at the registrar&apos;s DNS panel:
              <div className="mt-2 border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-xs mono">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-1.5">Type</th>
                      <th className="text-left px-3 py-1.5">Name / Host</th>
                      <th className="text-left px-3 py-1.5">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-slate-100">
                      <td className="px-3 py-1.5">A</td>
                      <td className="px-3 py-1.5">@</td>
                      <td className="px-3 py-1.5"><CopyPill value="76.76.21.21" testid="copy-vercel-a" /></td>
                    </tr>
                    <tr className="border-t border-slate-100">
                      <td className="px-3 py-1.5">CNAME</td>
                      <td className="px-3 py-1.5">www</td>
                      <td className="px-3 py-1.5"><CopyPill value={VERCEL_CNAME} testid="copy-vercel-cname" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                On Cloudflare set proxy status to <b>DNS only</b> (grey cloud) for both records —
                the orange cloud breaks Vercel&apos;s SSL handshake.
              </div>
            </Row>
            <Row n="4">
              Save. Come back to Vercel after 5–10 min — the domain badge will flip from{" "}
              <span className="mono text-amber-700">Invalid Configuration</span> to{" "}
              <span className="mono text-emerald-700">Valid</span> and Vercel auto-issues a free
              SSL certificate.
            </Row>
          </div>
        </Section>

        {/* Step 3 — Railway (backend) */}
        <Section
          icon={ServerCog}
          title="3. Point the API at Railway (backend)"
          subtitle={`We recommend a subdomain — ${apiSub || "api.your-domain.in"} — so it never clashes with the storefront.`}
          testid="domain-step-3"
        >
          <div className="space-y-3 text-sm text-slate-700">
            <Row n="1">
              Open the <b>Railway dashboard</b> →{" "}
              <a
                href="https://railway.app/dashboard"
                target="_blank"
                rel="noreferrer"
                className="text-[#0055FF] hover:underline inline-flex items-center gap-0.5"
              >
                railway.app/dashboard <ExternalLink className="w-3 h-3" />
              </a>{" "}
              → your DialFlow backend service → <b>Settings → Networking → Public Domains</b> →{" "}
              <b>Custom Domain</b>.
            </Row>
            <Row n="2">
              Enter <CopyPill value={apiSub || "api.your-domain.in"} testid="copy-apisub" />. Railway
              will return a CNAME target that looks like{" "}
              <span className="mono">something.up.railway.app</span> — copy that value.
            </Row>
            <Row n="3">
              Add this record at your registrar:
              <div className="mt-2 border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-xs mono">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-1.5">Type</th>
                      <th className="text-left px-3 py-1.5">Name / Host</th>
                      <th className="text-left px-3 py-1.5">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-slate-100">
                      <td className="px-3 py-1.5">CNAME</td>
                      <td className="px-3 py-1.5">api</td>
                      <td className="px-3 py-1.5">
                        <span className="text-slate-500">the value Railway gave you</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Row>
            <Row n="4">
              Wait until Railway shows <span className="mono text-emerald-700">Active</span>. Test in
              your browser:{" "}
              <span className="mono">{backendUrl}{RAILWAY_HEALTHCHECK}</span> should return a small
              JSON payload (or a 404 with a JSON body — that&apos;s fine, it proves the domain and
              SSL are working).
            </Row>
          </div>
        </Section>

        {/* Step 4 — Update env vars */}
        <Section
          icon={ShieldCheck}
          title="4. Wire the frontend to the new API + reload"
          subtitle="One env var + one CORS entry. Both take under a minute."
          testid="domain-step-4"
        >
          <div className="space-y-3 text-sm text-slate-700">
            <Row n="1">
              In Vercel → your project → <b>Settings → Environment Variables</b>, set:
              <div className="mt-1">
                <CopyPill value="REACT_APP_BACKEND_URL" testid="copy-env-key" /> ={" "}
                <CopyPill value={backendUrl || "https://api.your-domain.in"} testid="copy-env-val" />
              </div>
              Then trigger a redeploy from <b>Deployments → &hellip; menu → Redeploy</b> (React only
              picks up env vars at build time).
            </Row>
            <Row n="2">
              In Railway → your backend service → <b>Variables</b>, update{" "}
              <CopyPill value="CORS_ORIGINS" testid="copy-cors-key" /> so the browser is allowed to
              call the API from the new domain:
              <div className="mt-1">
                <CopyPill
                  value={
                    apex
                      ? `https://${apex},https://${www}`
                      : "https://your-domain.in,https://www.your-domain.in"
                  }
                  testid="copy-cors-val"
                />
              </div>
              Railway auto-restarts the service.
            </Row>
            <Row n="3">
              Open <span className="mono">{frontendUrl || "https://your-domain.in"}</span> in a
              private window. Sign in as an owner — the dashboard should load and the browser dev
              tools <b>Network</b> tab should show requests going to{" "}
              <span className="mono">{backendUrl || "https://api.your-domain.in"}</span>.
            </Row>
            <Row n="4">
              If a customer sees a CORS error or a mixed-content warning, double-check step 4.2
              (<span className="mono">CORS_ORIGINS</span> must include the exact scheme{" "}
              <span className="mono">https://</span> and no trailing slash).
            </Row>
          </div>
        </Section>

        {/* Optional — Razorpay callback URL */}
        <Section
          icon={ChevronRight}
          title="Optional · update Razorpay allowed origins"
          subtitle="Only needed if Razorpay flags cross-origin requests from the new domain."
          testid="domain-step-5"
        >
          <div className="space-y-2 text-sm text-slate-700">
            <Row n="1">
              Log in at{" "}
              <a
                href="https://dashboard.razorpay.com/app/website-app-settings"
                target="_blank"
                rel="noreferrer"
                className="text-[#0055FF] hover:underline inline-flex items-center gap-0.5"
              >
                dashboard.razorpay.com <ExternalLink className="w-3 h-3" />
              </a>{" "}
              → <b>Account &amp; Settings → Website / App details</b>.
            </Row>
            <Row n="2">
              Add <CopyPill value={frontendUrl || "https://your-domain.in"} testid="copy-rzp-domain" />{" "}
              to the &ldquo;Website URL&rdquo; list. Save.
            </Row>
          </div>
        </Section>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          variant="outline"
          onClick={async () => {
            const txt =
              `DialFlow — DNS records for ${apex}\n\n` +
              `Frontend (Vercel):\n` +
              `  A     @    76.76.21.21\n` +
              `  CNAME www  ${VERCEL_CNAME}\n\n` +
              `Backend (Railway):\n` +
              `  CNAME api  <value from Railway Networking tab>\n\n` +
              `Vercel env: REACT_APP_BACKEND_URL=${backendUrl}\n` +
              `Railway env: CORS_ORIGINS=${frontendUrl},https://${www}\n`;
            try {
              await navigator.clipboard.writeText(txt);
              toast.success("Full checklist copied to clipboard");
            } catch {
              toast.error("Couldn't copy — select the text on this page and copy manually.");
            }
          }}
          data-testid="domain-copy-all"
        >
          <Copy className="w-4 h-4 mr-1.5" /> Copy full DNS checklist
        </Button>
      </div>
    </div>
  );
}
